import React, { useState, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import '../css/CarDropdown.css';
import { FaChevronLeft, FaChevronRight, FaTimes, FaDownload, FaClipboardCheck, FaEye, FaImages } from 'react-icons/fa';
import WeatherWidget from '../components/WeatherWidget';
import LiveMap from '../components/LiveMap';
import { usePermissions } from '../hooks/usePermissions';
import { vehiclesAPI } from '../api/api';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

function fetchPhoto(photoId) {
  const token = localStorage.getItem('authToken');
  return fetch(
    `${API_BASE_URL}/get-photo/${photoId}`,
    { headers: { Authorization: `Bearer ${token}` } }
  )
    .then(res => res.json())
    .then(r => (r.success ? r.data : null))
    .catch(() => null);
}

function PhotoGalleryLightbox({ gallery, onClose }) {
  const [idx, setIdx] = useState(0);
  const [cache, setCache] = useState({});
  const [loading, setLoading] = useState(false);
  const photos = gallery.photos || [];

  const loadPhoto = useCallback((i) => {
    const p = photos[i];
    if (!p || cache[p.id]) return;
    setLoading(true);
    fetchPhoto(p.id).then(data => {
      if (data) {
        setCache(prev => ({ ...prev, [p.id]: data }));
      }
      setLoading(false);
    });
  }, [photos, cache]);

  useEffect(() => { loadPhoto(idx); }, [idx, loadPhoto]);
  useEffect(() => {
    if (idx < photos.length - 1) loadPhoto(idx + 1);
  }, [idx, photos.length, loadPhoto]);

  const goNext = useCallback(() => {
    if (idx < photos.length - 1) setIdx(idx + 1);
  }, [idx, photos.length]);

  const goPrev = useCallback(() => {
    if (idx > 0) setIdx(idx - 1);
  }, [idx]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'ArrowRight') goNext();
      if (e.key === 'ArrowLeft') goPrev();
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [goNext, goPrev, onClose]);

  const cur = photos[idx];
  const cached = cur ? cache[cur.id] : null;

  return ReactDOM.createPortal(
    <div
      className="gallery-lightbox-overlay"
      onClick={onClose}
    >
      <div
        className="gallery-lightbox-content"
        onClick={e => e.stopPropagation()}
      >
        <button
          className="gallery-lightbox-close"
          onClick={onClose}
        >
          &times;
        </button>
        {loading && !cached ? (
          <div className="gallery-lightbox-placeholder">
            Betöltés...
          </div>
        ) : cached ? (
          <img
            src={
              `data:image/${cached.extension}`
              + `;base64,${cached.content}`
            }
            alt={cached.filename}
            className="gallery-lightbox-img"
          />
        ) : (
          <div className="gallery-lightbox-placeholder">
            {cur?.filename || 'Kép nem elérhető'}
          </div>
        )}
        <div className="gallery-lightbox-caption">
          {cur?.filename}
          <span className="gallery-lightbox-counter">
            {idx + 1} / {photos.length}
          </span>
        </div>
        <div className="gallery-lightbox-nav">
          <button
            className="gallery-lightbox-nav-btn"
            onClick={goPrev}
            disabled={idx === 0}
          >
            &lsaquo;
          </button>
          <button
            className="gallery-lightbox-nav-btn"
            onClick={goNext}
            disabled={idx === photos.length - 1}
          >
            &rsaquo;
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

const CarDropdown = ({ car }) => {
  const [mainImgIdx, setMainImgIdx] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [documents, setDocuments] = useState(null);
  const [loadingDocuments, setLoadingDocuments] = useState(false);
  const [handoverModalOpen, setHandoverModalOpen] = useState(false);
  const [selectedHandover, setSelectedHandover] = useState(null);
  const [activeGallery, setActiveGallery] = useState(null);
  const [mounted, setMounted] = useState(false);
  const { permissions } = usePermissions();

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    const loadDocuments = async () => {
      if (!car || !car.id) return;

      try {
        setLoadingDocuments(true);
        const docData = await vehiclesAPI.getVehicleDocuments(car.id);
        setDocuments(docData.sections);
      } catch (error) {
        console.error('Error loading documents:', error);
        setDocuments({});
      } finally {
        setLoadingDocuments(false);
      }
    };

    loadDocuments();
  }, [car?.id]);

  const handleDownload = (e, file) => {
    e.preventDefault();

    try {
      if (!file.content) {
        alert('A fájl tartalma nem érhető el');
        return;
      }

      // Decode Base64 content
      const binaryString = window.atob(file.content);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }

      // Create blob and download
      const blob = new Blob([bytes], { type: file.mime || 'application/octet-stream' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = file.extension ? `${file.filename}.${file.extension}` : file.filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Download error:', error);
      alert('Hiba a fájl letöltésekor');
    }
  };

  const handleViewHandover = (e, file) => {
    e.preventDefault();
    if (file.handover_data) {
      setSelectedHandover(file.handover_data);
      setHandoverModalOpen(true);
    }
  };

  const handleZipDownload = (e, gallery) => {
    e.preventDefault();
    const token = localStorage.getItem('authToken');
    const url = `${API_BASE_URL}${gallery.download_zip_url}`;
    fetch(url, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(res => res.blob())
      .then(blob => {
        const blobUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = `${gallery.name}.zip`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(blobUrl);
      })
      .catch(() => alert('Hiba a ZIP letöltésekor'));
  };

  const formatHandoverValue = (value, type = 'text') => {
    if (value === null || value === undefined || value === '') return '-';
    if (type === 'boolean') return value ? 'Igen' : 'Nem';
    if (type === 'date' && value) {
      return new Date(value).toLocaleDateString('hu-HU');
    }
    return value;
  };


  if (!car) return null;
  const gallery = car.gallery || [];
  const showImg = gallery[mainImgIdx] || gallery[0];

  const prevImg = (e) => {
    e.stopPropagation();
    setMainImgIdx(idx => (idx - 1 + gallery.length) % gallery.length);
  };
  const nextImg = (e) => {
    e.stopPropagation();
    setMainImgIdx(idx => (idx + 1) % gallery.length);
  };

  return (
    <div className="car-dropdown-inline-content">
      <div className="car-dropdown-main">
        <div className="car-dropdown-left">
          <div className="car-dropdown-section">
            <h4>Adatok</h4>
            <div className="car-dropdown-info-grid">
              <div>Ügyfél</div><div>{car.company}</div>
              <div>Alvázszám</div><div>{car.chassis_number17}</div>
              <div>Rendszám</div><div>{car.plate_number}</div>
              <div>Típus</div><div>{car.brand}</div>
              <div>Kilométerek</div><div>{car.monthly_distance}</div>
              <div>Státusz</div><div>{car.status_info?.status_name || car.statusText || 'Nincs státusz'}</div>
              <div>Kerék típus</div><div>{car.wheelType}</div>
              <div>Kiállító</div><div>{car.issuer}</div>
              <div>Kiállító adószáma</div><div>{car.issuerTax}</div>
              <div>Kiállítási dátum</div><div>{car.issuedDate}</div>
            </div>
            {permissions.can_see_obd && (
              <div className="car-dropdown-section">
                <h5>Autó adatok</h5>
                <div className="car-dropdown-info-grid">
                  <div>Kilométer óra</div><div>{car.km_data?.km_current ? `${Math.round(car.km_data.km_current)} km` : car.obd.km}</div>
                  <div>Üzemanyag szint</div><div>{car.obd.fuelLevel}</div>
                  <div>Átlag fogyasztás</div><div>{car.obd.avgConsumption}</div>
                  <div>Pillanatnyi fogyasztás</div><div>{car.obd.instantConsumption}</div>
                </div>
              </div>
            )}
            {permissions.can_see_documents && (
              <div className="car-dropdown-docs">
                <h5>Dokumentumok</h5>
                {loadingDocuments ? (
                  <p className="car-dropdown-docs-loading">Betöltés...</p>
                ) : documents && Object.keys(documents).length > 0 ? (
                  Object.entries(documents).map(([sectionKey, section]) => (
                    <div key={sectionKey} className="car-dropdown-docs-section">
                      <h6 className="car-dropdown-docs-section-title">
                        {section.name}
                      </h6>
                      <ul className="car-dropdown-docs-list">
                        {(section.photo_galleries || []).map((gallery, gIdx) => (
                          <li
                            key={`gallery-${gIdx}`}
                            className="car-dropdown-docs-item car-dropdown-docs-item-gallery"
                          >
                            <FaImages className="car-dropdown-docs-gallery-icon" />
                            <span className="car-dropdown-docs-link">
                              {gallery.name}
                            </span>
                            <span className="car-dropdown-docs-badge-gallery">
                              {gallery.photo_count} fotó
                            </span>
                            {gallery.uploaded_at && (
                              <span className="car-dropdown-docs-date">
                                {gallery.uploaded_at.substring(0, 16)}
                              </span>
                            )}
                            <FaEye
                              className="car-dropdown-docs-view-icon"
                              onClick={() => setActiveGallery(gallery)}
                              style={{cursor: 'pointer'}}
                              title="Fotók megtekintése"
                            />
                            <FaDownload
                              className="car-dropdown-docs-download-icon"
                              onClick={(e) => handleZipDownload(e, gallery)}
                              style={{cursor: 'pointer'}}
                              title="ZIP letöltés"
                            />
                          </li>
                        ))}
                        {section.files.map((file) => {
                          const isForm = file.is_form;
                          const fullFilename = file.extension
                            ? `${file.filename}.${file.extension}`
                            : file.filename;
                          const dateStr = file.uploaded_at
                            ? file.uploaded_at.substring(0, 16)
                            : '';

                          return (
                            <li key={file.id} className={`car-dropdown-docs-item ${isForm ? 'car-dropdown-docs-item-form' : ''}`}>
                              {isForm && (
                                <FaClipboardCheck className="car-dropdown-docs-form-icon" />
                              )}
                              <span className="car-dropdown-docs-link">
                                {fullFilename}
                              </span>
                              {dateStr && (
                                <span className="car-dropdown-docs-date">
                                  {dateStr}
                                </span>
                              )}
                              {isForm && file.handover_data && (
                                <FaEye
                                  className='car-dropdown-docs-view-icon'
                                  onClick={(e) => handleViewHandover(e, file)}
                                  style={{cursor: 'pointer'}}
                                  title="Megtekintés"
                                />
                              )}
                              {file.content && (
                                <FaDownload
                                  className='car-dropdown-docs-download-icon'
                                  onClick={(e) => handleDownload(e, file)}
                                  style={{cursor: 'pointer'}}
                                  title="Letöltés"
                                />
                              )}
                              {isForm && file.handover_data && (
                                <span className="car-dropdown-docs-badge-filled">Kitöltve</span>
                              )}
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  ))
                ) : (
                  <p className="car-dropdown-docs-empty">Nincs dokumentum</p>
                )}
              </div>
            )}
          </div>
        </div>
        <div className="car-dropdown-center">
          {permissions.can_see_map && (
            <div className="car-dropdown-map">
              <LiveMap
                height="220px"
                width="100%"
                vehiclePosition={car.latest_position}
              />
            </div>
          )}
          <div className="car-dropdown-mainimg-wrapper">
            <img
              src={showImg}
              alt={car.model}
              className="car-dropdown-mainimg"
              onClick={() => setLightboxOpen(true)}
            />
            {gallery.length > 1 && (
              <>
                <button className="car-dropdown-imgnav left" onClick={prevImg}><FaChevronLeft /></button>
                <button className="car-dropdown-imgnav right" onClick={nextImg}><FaChevronRight /></button>
              </>
            )}
          </div>
          <div className="car-dropdown-gallery">
            {gallery.map((img, i) => (
              <img
                src={img}
                alt="gallery"
                key={i}
                className={i === mainImgIdx ? 'active' : ''}
                onClick={() => setMainImgIdx(i)}
              />
            ))}
          </div>
        </div>
        {/* <div className="car-dropdown-right">
          <div className="car-dropdown-section">
            <h4>OBD Adatok</h4>
            <div className="car-dropdown-info-grid">
              <div>Pillanatnyi fogyasztás</div><div>{car.obd.instantConsumption}</div>
              <div>Átlag fogyasztás</div><div>{car.obd.avgConsumption}</div>
              <div>Üzemanyagszint</div><div>{car.obd.fuelLevel}</div>
              <div>Km állás</div><div>{car.obd.km}</div>
              <div>Megtehető táv</div><div>{car.obd.doable_distance}</div>
              <div>Mozgásban</div><div>{car.obd.moving ? 'Igen' : 'Nem'}</div>
              <div>Egy helyben</div><div>{car.obd.stopped ? 'Igen' : 'Nem'}</div>
            </div>
          </div>
        </div> */}
      </div>

      <div className="car-dropdown-weather">
        <WeatherWidget
          coordinates={car.latest_position ? {
            lat: car.latest_position.latitude,
            lon: car.latest_position.longitude
          } : null}
        />
      </div>
      
      {lightboxOpen && (
        <div className="car-dropdown-lightbox" onClick={() => setLightboxOpen(false)}>
          <div style={{ position: 'relative', display: 'inline-block' }}>
            <img src={showImg} alt={car.model} className="car-dropdown-lightbox-img" />
            <button className="car-dropdown-lightbox-close" onClick={e => { e.stopPropagation(); setLightboxOpen(false); }}><FaTimes /></button>
          </div>
          {gallery.length > 1 && (
            <>
              <button className="car-dropdown-lightbox-nav left" onClick={e => { e.stopPropagation(); prevImg(e); }}><FaChevronLeft /></button>
              <button className="car-dropdown-lightbox-nav right" onClick={e => { e.stopPropagation(); nextImg(e); }}><FaChevronRight /></button>
            </>
          )}
        </div>
      )}

      {activeGallery && (
        <PhotoGalleryLightbox
          gallery={activeGallery}
          onClose={() => setActiveGallery(null)}
        />
      )}

      {/* Handover (Átadási jegyzőkönyv) Modal - Portal to body */}
      {mounted && handoverModalOpen && selectedHandover && ReactDOM.createPortal(
        <div className="car-dropdown-handover-modal-overlay" onClick={() => setHandoverModalOpen(false)}>
          <div className="car-dropdown-handover-modal" onClick={e => e.stopPropagation()}>
            <div className="car-dropdown-handover-modal-header">
              <h5><FaClipboardCheck /> Átadási jegyzőkönyv</h5>
              <button className="car-dropdown-handover-modal-close" onClick={() => setHandoverModalOpen(false)}>
                <FaTimes />
              </button>
            </div>
            <div className="car-dropdown-handover-modal-body">
              <div className="car-dropdown-handover-section">
                <h6>Alapadatok</h6>
                <div className="car-dropdown-handover-grid">
                  <div className="car-dropdown-handover-label">Átadó neve</div>
                  <div className="car-dropdown-handover-value">{formatHandoverValue(selectedHandover.handover_person_name)}</div>

                  <div className="car-dropdown-handover-label">Átadás helyszíne</div>
                  <div className="car-dropdown-handover-value">{formatHandoverValue(selectedHandover.handover_location)}</div>

                  <div className="car-dropdown-handover-label">Km állapot átadáskor</div>
                  <div className="car-dropdown-handover-value">{formatHandoverValue(selectedHandover.km_at_handover)} km</div>

                  <div className="car-dropdown-handover-label">Üzemanyagszint</div>
                  <div className="car-dropdown-handover-value">{formatHandoverValue(selectedHandover.fuel_level)}%</div>

                  <div className="car-dropdown-handover-label">Szennyeződési szint</div>
                  <div className="car-dropdown-handover-value">{formatHandoverValue(selectedHandover.cleanliness_label)}</div>

                  <div className="car-dropdown-handover-label">Létrehozva</div>
                  <div className="car-dropdown-handover-value">{formatHandoverValue(selectedHandover.created_at, 'date')}</div>
                </div>
              </div>

              <div className="car-dropdown-handover-section">
                <h6>Tartozékok</h6>
                <div className="car-dropdown-handover-grid">
                  <div className="car-dropdown-handover-label">Töltő</div>
                  <div className="car-dropdown-handover-value">{formatHandoverValue(selectedHandover.has_charger, 'boolean')}</div>

                  {!!selectedHandover.has_charger && selectedHandover.charger_serial && (
                    <>
                      <div className="car-dropdown-handover-label">Töltő azonosító</div>
                      <div className="car-dropdown-handover-value">{formatHandoverValue(selectedHandover.charger_serial)}</div>
                    </>
                  )}

                  <div className="car-dropdown-handover-label">Forgalmi</div>
                  <div className="car-dropdown-handover-value">{formatHandoverValue(selectedHandover.has_registration_card, 'boolean')}</div>

                  <div className="car-dropdown-handover-label">Pótkulcs</div>
                  <div className="car-dropdown-handover-value">{formatHandoverValue(selectedHandover.has_spare_key, 'boolean')}</div>

                  <div className="car-dropdown-handover-label">OBD</div>
                  <div className="car-dropdown-handover-value">{formatHandoverValue(selectedHandover.has_obd, 'boolean')}</div>

                  {!!selectedHandover.has_obd && selectedHandover.obd_imei && (
                    <>
                      <div className="car-dropdown-handover-label">OBD IMEI</div>
                      <div className="car-dropdown-handover-value">{formatHandoverValue(selectedHandover.obd_imei)}</div>
                    </>
                  )}
                </div>
              </div>

              <div className="car-dropdown-handover-section">
                <h6>Autópálya matrica</h6>
                <div className="car-dropdown-handover-grid">
                  <div className="car-dropdown-handover-label">Matrica érvényessége</div>
                  <div className="car-dropdown-handover-value">{formatHandoverValue(selectedHandover.highway_ticket_expiry, 'date')}</div>

                  <div className="car-dropdown-handover-label">Igényli a szolgáltatást</div>
                  <div className="car-dropdown-handover-value">{formatHandoverValue(selectedHandover.needs_highway_ticket, 'boolean')}</div>
                </div>
              </div>

              <div className="car-dropdown-handover-section">
                <h6>Állapot</h6>
                <div className="car-dropdown-handover-grid">
                  <div className="car-dropdown-handover-label">Látható sérülés</div>
                  <div className="car-dropdown-handover-value">{formatHandoverValue(selectedHandover.visible_damage) || 'Nincs'}</div>

                  <div className="car-dropdown-handover-label">Baleseti bejelentő</div>
                  <div className="car-dropdown-handover-value">{formatHandoverValue(selectedHandover.received_accident_report, 'boolean')}</div>
                </div>
              </div>

              {selectedHandover.notes && (
                <div className="car-dropdown-handover-section">
                  <h6>Megjegyzés</h6>
                  <div className="car-dropdown-handover-notes">{selectedHandover.notes}</div>
                </div>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default CarDropdown; 